import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import { CommonButton } from '../../../../Styles/CommonStyles';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import { useAlert } from '../../../ui/AlertProvider';
import { useUpdateConsumableReqMutation } from '../../../../features/consumablerequest/consumableReqApiSlice';
import nameStringFn from '../../../../data/nameStringFn';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';

// Styled components defined outside the component for better performance
const StyledButton = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const ForwardButton = styled(StyledButton)`
  background-color: #4caf50;
`;

const ReturnButton = styled(StyledButton)`
  background-color: #ff9800;
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-bottom: 20px;
  margin-top: 20px;
`;

const StyledTableHead = styled(TableHead)`
  background-color: #1976d2;
  
  & th {
    color: white;
    font-weight: bold;
  }
`;

const SelectedAssetsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const SelectedAssetsTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SelectedAssetsTitle = styled.h3`
  margin-bottom: 0;
  color: #1976d2;
`;

const SummaryContainer = styled.div`
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 5px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  padding: 5px 10px;
  border-radius: 5px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const SummaryItemName = styled.span`
  font-weight: 500;
  margin-right: 10px;
`;

const SummaryItemCount = styled.span`
  background-color: #1976d2;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.8rem;
`;

const NotificationMessage = styled.div`
  margin-top: 10px;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  display: flex;
  align-items: center;
  background-color: ${props => props.isPartial ? '#ff9800' : '#4caf50'};
  color: white;
  margin-bottom: 20px;
`;

// Error handling components
const ErrorMessage = styled.div`
  background-color: #f44336;
  color: white;
  padding: 10px 15px;
  border-radius: 4px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ErrorCloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  padding: 0 5px;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LoadingText = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 10px;
`;

// Memoized summary component
const ConsumablesSummary = React.memo(({ items, generateSummary }) => (
  <SummaryContainer>
    <span style={{ fontWeight: 'bold', marginRight: '10px' }}>
      Total: {items.reduce((sum, item) => sum + parseInt(item.selectedQuantity || item.quantity), 0)} items
    </span>
    {generateSummary(items).map((item, index) => (
      <SummaryItem key={index}>
        <SummaryItemName>{item.name}</SummaryItemName>
        <SummaryItemCount>{item.count}</SummaryItemCount>
      </SummaryItem>
    ))}
  </SummaryContainer>
));

// Memoized table component
const ConsumablesTable = React.memo(({ items, getRequestedQuantity }) => (
  <Table>
    <StyledTableHead>
      <TableRow>
        <TableCell>Sl.No.</TableCell>
        <TableCell>Consumable</TableCell>
        <TableCell>Asset</TableCell>
        <TableCell>Asset Tag</TableCell>
        <TableCell>Part No</TableCell>
        <TableCell>Total Items Requested</TableCell>
        <TableCell>Issued at BO</TableCell>
        <TableCell>Issued at Store</TableCell>
      </TableRow>
    </StyledTableHead>
    <TableBody>
      {items.map((item, index) => (
        <TableRow key={index}>
          <TableCell>{index + 1}</TableCell>
          <TableCell>{item.consumable || '-'}</TableCell>
          <TableCell>{item.asset || 'Not Applicable'}</TableCell>
          <TableCell>{item.assettag?.label || 'Not Applicable'}</TableCell>
          <TableCell>{item.partno || '-'}</TableCell>
          <TableCell>{item.modReqQuantity || item.originalQuantity || getRequestedQuantity(item) || '-'}</TableCell>
          <TableCell>{item.boIssuedQuantity || 0}</TableCell>
          <TableCell>{item.selectedQuantity || item.quantity || '-'}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
));

const StoreL2InboxFormCIR = ({ row, handleBack }) => {
  const [remark, setRemark] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [latestConsumables, setLatestConsumables] = useState([]);
  const [isPartialIssue, setIsPartialIssue] = useState(false);
  
  // Auth and office data
  const auth = useAuth();
  const alert = useAlert();
  const user_current_office = useSelector((state) => state.office.off);
  
  // API queries with better error handling
  const { getSingleuser, isError: isUsersError, error: usersError } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data, isError, error }) => ({
      getSingleuser: data?.entities,
      isError,
      error
    }),
  });

  const { officedata, isError: isOfficesError, error: officesError } = useGetofficesQuery('offices', {
    selectFromResult: ({ data, isError, error }) => ({
      officedata: data?.entities,
      isError,
      error
    }),
  });

  const { storeoffices, isError: isStoreOfficesError, error: storeOfficesError } = useGetStoreOfficesQuery('storeoffices', {
    selectFromResult: ({ data, isError, error }) => ({
      storeoffices: data?.entities,
      isError,
      error
    }),
  });
  
  // Mutations with better error handling
  const [updateConsumableReq, { isLoading: isUpdatingReq, isError: isUpdateError, error: updateError }] = useUpdateConsumableReqMutation();

  // Set general error state when any API error occurs
  useEffect(() => {
    if (isUsersError) {
      setError(`Error fetching users: ${usersError?.data?.message || 'Unknown error'}`);
    } else if (isOfficesError) {
      setError(`Error fetching offices: ${officesError?.data?.message || 'Unknown error'}`);
    } else if (isStoreOfficesError) {
      setError(`Error fetching store offices: ${storeOfficesError?.data?.message || 'Unknown error'}`);
    } else if (isUpdateError) {
      setError(`Error updating request: ${updateError?.data?.message || 'Unknown error'}`);
    } else {
      setError('');
    }
  }, [
    isUsersError, usersError,
    isOfficesError, officesError,
    isStoreOfficesError, storeOfficesError,
    isUpdateError, updateError
  ]);
  
  const storeL1_id = useMemo(() => 
    storeoffices ? Object.values(storeoffices).find(item => item?.storeoffice === row?.req_store)?.roleUsers?.L1ReqApprover_C : "",
    [storeoffices, row?.req_store]
  );

  // Generate summary data from consumables - memoized
  const generateSummary = useCallback((items) => {
    if (!items || items.length === 0) return [];

    const summary = items.reduce((acc, item) => {
      const { consumable } = item;
      if (!acc[consumable]) {
        acc[consumable] = 0;
      }
      acc[consumable] += parseInt(item.quantity || item.selectedQuantity);
      return acc;
    }, {});

    return Object.entries(summary).map(([name, count]) => ({ name, count }));
  }, []);

  // Get the latest store consumable issues - memoized
  const getLatestStoreConsumIssue = useCallback(() => {
    if (row?.store_consum_issue && row.store_consum_issue.length > 0) {
      const latestIssue = row.store_consum_issue[row.store_consum_issue.length - 1];
      return latestIssue.data || [];
    }
    return [];
  }, [row?.store_consum_issue]);
  
  // Get the requested quantity for an item from the last consumables_requested object - memoized
  // This should show the remaining quantity after BO has issued partial
  const getRequestedQuantity = useCallback((item) => {
    if (!row?.consumables_requested || row.consumables_requested.length === 0) {
      return item.quantity;
    }
    
    const latestRequest = row.consumables_requested[row.consumables_requested.length - 1];
    if (!latestRequest?.data) {
      return item.quantity;
    }
    
    // Find the original requested item
    const requestedItem = latestRequest.data.find(reqItem => 
      reqItem.consumable === item.consumable && 
      reqItem.asset === item.asset &&
      (reqItem.assettag?.label === item.assettag?.label)
    );
    
    if (!requestedItem) {
      return item.quantity;
    }
    
    const originalQuantity = parseInt(requestedItem.quantity, 10);
    
    // Check if BO has issued partial consumables
    if (row?.bo_consum_issue && row.bo_consum_issue.length > 0) {
      const latestBoIssue = row.bo_consum_issue[row.bo_consum_issue.length - 1];
      if (latestBoIssue?.data) {
        // Find all BO issued items matching this request (BO might split into multiple part numbers)
        const boIssuedItems = latestBoIssue.data.filter(boItem => 
          boItem.consumable === item.consumable && 
          boItem.asset === item.asset &&
          (boItem.assettag?.label === item.assettag?.label)
        );
        
        // Calculate total BO issued quantity
        const totalBoIssuedQty = boIssuedItems.reduce((sum, boItem) => 
          sum + parseInt(boItem.quantity, 10), 0
        );
        
        // Return remaining quantity (original - BO issued)
        return originalQuantity - totalBoIssuedQty;
      }
    }
    
    return originalQuantity;
  }, [row?.consumables_requested, row?.bo_consum_issue]);

  // Initialize latest consumables and calculate partial status
  useEffect(() => {
    const fetchedConsumables = getLatestStoreConsumIssue();
    setLatestConsumables(fetchedConsumables);
  }, [getLatestStoreConsumIssue]);

  // Calculate partial issue status whenever latest consumables change
  useEffect(() => {
    if (!latestConsumables || latestConsumables.length === 0) {
      setIsPartialIssue(false);
      return;
    }

    // Check if all requested quantities are being fulfilled
    let isPartial = false;
    
    for (const item of latestConsumables) {
      const requestedQty = getRequestedQuantity(item);
      const issuedQty = parseInt(item.selectedQuantity || item.quantity, 10);
      
      // If any item has issued quantity less than requested, it's partial
      if (issuedQty < requestedQty) {
        isPartial = true;
        break;
      }
    }
    
    // Also check if not all requested items are in the issued list
    if (!isPartial && row?.consumables_requested && row.consumables_requested.length > 0) {
      const latestRequest = row.consumables_requested[row.consumables_requested.length - 1];
      if (latestRequest?.data) {
        // Calculate remaining items (after BO issue)
        const remainingItems = [];
        
        for (const reqItem of latestRequest.data) {
          const originalQty = parseInt(reqItem.quantity, 10);
          let boIssuedQty = 0;
          
          // Check BO issued quantity
          if (row?.bo_consum_issue && row.bo_consum_issue.length > 0) {
            const latestBoIssue = row.bo_consum_issue[row.bo_consum_issue.length - 1];
            if (latestBoIssue?.data) {
              const boIssuedItems = latestBoIssue.data.filter(boItem => 
                boItem.consumable === reqItem.consumable && 
                boItem.asset === reqItem.asset &&
                (boItem.assettag?.label === reqItem.assettag?.label)
              );
              boIssuedQty = boIssuedItems.reduce((sum, boItem) => 
                sum + parseInt(boItem.quantity, 10), 0
              );
            }
          }
          
          const remainingQty = originalQty - boIssuedQty;
          if (remainingQty > 0) {
            remainingItems.push({
              consumable: reqItem.consumable,
              asset: reqItem.asset,
              assettag: reqItem.assettag,
              quantity: remainingQty
            });
          }
        }
        
        // Check if all remaining items are in the issued list
        if (remainingItems.length > latestConsumables.length) {
          isPartial = true;
        }
      }
    }
    
    setIsPartialIssue(isPartial);
  }, [latestConsumables, getRequestedQuantity, row?.consumables_requested, row?.bo_consum_issue]);

  // Handle returning the request to Store Level 1 for modification - memoized
  const handleReturnToStoreL1 = useCallback(async () => {
    if (!remark.trim()) {
      alert.showWarning("Please provide a remark explaining what needs to be modified.");
      return;
    }
    
    try {
      setIsLoading(true);
      
      const senttoData = {
        sent_to_tag: "storel2_bts_return_l1",
        Text: remark,
        sent_by: { 
          id: auth?.id, 
          name: getSingleuser?.[auth?.id]?.name, 
          office: user_current_office, 
          designation: auth?.designation?.[user_current_office] 
        },
        sent_to: { 
          id: storeL1_id, 
          name: getSingleuser?.[storeL1_id]?.name, 
          office: user_current_office, 
          designation: getSingleuser?.[storeL1_id]?.designation?.[user_current_office] 
        },
        action: "Request returned to Store Level 1 for modification",
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[storeL1_id])}-${user_current_office}-${getSingleuser?.[storeL1_id]?.designation?.[user_current_office] || 'Unknown'}`,
        time_stamp: new Date()
      };
      
      const body = { id: row?._id, senttoData: senttoData };
      
      await updateConsumableReq(body).unwrap();
      alert.showSuccess("Request returned to Store Level 1 for modification.", "Success", handleBack);
    } catch (err) {
      setError(`Failed to return request: ${err.message || 'Unknown error'}`);
      console.error("Error returning request:", err);
      alert.showError("There was an error returning the request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [
    remark, auth, getSingleuser, user_current_office, 
    storeL1_id, row?._id, updateConsumableReq, handleBack, alert
  ]);

  // Handle forwarding the request to User for acceptance - memoized
  const handleForwardToUser = useCallback(async () => {
    console.log("Entered")
    if (!remark.trim()) {
      alert.showWarning("Please provide a remark before forwarding.");
      return;
    }
    
    try {
      setIsLoading(true);
      console.log("Entered 2")
      const senttoData = {
        sent_to_tag: "storel2_cir_forward_user",
        Text: remark,
        sent_by: { 
          id: auth?.id, 
          name: getSingleuser?.[auth?.id]?.name, 
          office: user_current_office, 
          designation: auth?.designation?.[user_current_office] 
        },
        sent_to: { 
          id: row.req_creator_id, 
          name: getSingleuser?.[row.req_creator_id]?.name, 
          office: row?.req_data[0]?.sent_by?.office, 
          designation: getSingleuser?.[row.req_creator_id]?.designation?.[row?.req_data[0]?.sent_by?.office] 
        },
        action: `${isPartialIssue ? 'Partial' : 'Full'} consumables issue approved and forwarded to User for acceptance`,
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[row.req_creator_id])}-${row?.req_data[0]?.sent_by?.office}-${getSingleuser?.[row.req_creator_id]?.designation?.[row?.req_data[0]?.sent_by?.office] || 'Unknown'}`,
        time_stamp: new Date()
      };
      console.log("Entered 3")
      const body = { id: row?._id, senttoData: senttoData };
      
      await updateConsumableReq(body).unwrap();
      console.log("Entered4")
      alert.showSuccess(`${isPartialIssue ? 'Partial' : 'Full'} consumables issue approved and forwarded to User for final acceptance.`, "Success", handleBack);
    } catch (err) {
      setError(`Failed to forward request: ${err.message || 'Unknown error'}`);
      console.error("Error forwarding request:", err);
      alert.showError("There was an error forwarding the request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [
    remark, auth, getSingleuser, user_current_office, 
    isPartialIssue, row?._id, updateConsumableReq, handleBack, alert
  ]);

  return (
    <>
      {error && (
        <ErrorMessage>
          {error}
          <ErrorCloseButton onClick={() => setError('')}>×</ErrorCloseButton>
        </ErrorMessage>
      )}
      
      {isLoading && (
        <LoadingOverlay>
          <LoadingText>
            <CircularProgress size={24} /> 
            Processing request...
          </LoadingText>
        </LoadingOverlay>
      )}

      {/* Remarks textarea at the top */}
      <RemarkContainer>
        <TextField
          fullWidth
          label="Remarks"
          variant="outlined"
          multiline
          rows={4}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Add your remarks here..."
          disabled={isLoading}
        />
      </RemarkContainer>
      
      {/* Display the issued consumables */}
      {latestConsumables.length > 0 && (
        <SelectedAssetsContainer>
          <SelectedAssetsTitleContainer>
            <SelectedAssetsTitle>Consumables Issued by Store Level 1</SelectedAssetsTitle>
          </SelectedAssetsTitleContainer>

          {/* Summary section */}
          <ConsumablesSummary items={latestConsumables} generateSummary={generateSummary} />
          
          <StyledTableContainer component={Paper}>
            <ConsumablesTable items={latestConsumables} getRequestedQuantity={getRequestedQuantity} />
          </StyledTableContainer>
          
          {/* Notification message */}
          <NotificationMessage isPartial={isPartialIssue}>
            {isPartialIssue 
              ? 'Partial quantities selected for issue. Some requested items or quantities will not be issued.' 
              : 'All remaining requested items selected for issue with full quantities.'}
          </NotificationMessage>
        </SelectedAssetsContainer>
      )}
      
      {/* Action buttons */}
      <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        <ForwardButton 
          onClick={handleForwardToUser} 
          disabled={isLoading || latestConsumables.length === 0}
        >
          Approve & Forward to User for Acceptance
        </ForwardButton>
        
        <ReturnButton 
          onClick={handleReturnToStoreL1} 
          disabled={isLoading}
        >
          Return to Store Level 1
        </ReturnButton>
      </div>
    </>
  );
};

// Apply memo to component before exporting
const MemoizedStoreL2InboxFormCIR = React.memo(StoreL2InboxFormCIR);
export default MemoizedStoreL2InboxFormCIR;